import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { Button, IconButton, Stepper } from '@/design-system'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import { WIZARD_STEP_HINTS, WIZARD_STEP_LABELS, WIZARD_STEPS, type WizardStepId } from '../../types'

export interface WizardStepMeta {
  id: WizardStepId
  label: string
  sub?: string
}

export interface WizardShellProps {
  step: WizardStepId
  stepIndex: number
  /** Defaults to the full WIZARD_STEPS list with their step hints */
  steps?: WizardStepMeta[]
  /** Enables click-to-jump in the step rail. Omit to render a static (non-interactive) rail. */
  onStepSelect?: (id: WizardStepId) => void
  /** Live "what this will look like" card — rendered in a sticky right rail on desktop, above content on mobile */
  preview?: React.ReactNode
  title?: string
  subtitle?: string
  canContinue?: boolean
  continueLabel?: string
  backLabel?: string
  hideContinue?: boolean
  saving?: boolean
  onBack: () => void
  onContinue: () => void
  onClose: () => void
  children: React.ReactNode
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

const Root = styled(Box)({
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--ds-canvas)',
  color: 'var(--ds-ink)',
  fontFamily: 'var(--ds-font)',
})

const TopBar = styled(Box)({
  position: 'sticky',
  top: 0,
  zIndex: 10,
  background: 'color-mix(in srgb, var(--ds-canvas) 88%, transparent)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--ds-border)',
  padding: 'var(--ds-space-3) var(--ds-space-5)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--ds-space-3)',
  minHeight: 40,
})

const MobileStepStrip = styled(Box)({
  padding: 'var(--ds-space-3) var(--ds-space-5) 0',
  maxWidth: 960,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
})

const Frame = styled(Box)({
  flex: 1,
  display: 'flex',
  padding: 'var(--ds-space-6) var(--ds-space-5)',
  maxWidth: 1240,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
})

const Rail = styled(Box)({
  width: 220,
  flexShrink: 0,
  paddingTop: 'var(--ds-space-2)',
})

const Main = styled(Box)({
  flex: 1,
  minWidth: 0,
  animation: `${fadeUp} 420ms var(--ds-ease) both`,
})

const PreviewRail = styled(Box)({
  width: 300,
  flexShrink: 0,
  position: 'sticky',
  top: 96,
  alignSelf: 'flex-start',
})

const Footer = styled(Box)({
  position: 'sticky',
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--ds-space-3)',
  padding: 'var(--ds-space-4) var(--ds-space-5)',
  paddingBottom: 'max(var(--ds-space-4), env(safe-area-inset-bottom))',
  background: 'color-mix(in srgb, var(--ds-canvas) 92%, transparent)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderTop: '1px solid var(--ds-border)',
})

const DEFAULT_STEPS: WizardStepMeta[] = WIZARD_STEPS.map((id) => ({
  id,
  label: WIZARD_STEP_LABELS[id],
  sub: WIZARD_STEP_HINTS[id],
}))

export function WizardShell({
  step,
  stepIndex,
  steps = DEFAULT_STEPS,
  onStepSelect,
  preview,
  title,
  subtitle,
  canContinue = true,
  continueLabel = 'Continue',
  backLabel = 'Back',
  hideContinue,
  saving,
  onBack,
  onContinue,
  onClose,
  children,
}: WizardShellProps) {
  const heading = title || WIZARD_STEP_LABELS[step]
  const isFirst = stepIndex <= 0
  const handleStepClick = onStepSelect
    ? (id: string) => onStepSelect(id as WizardStepId)
    : undefined

  return (
    <Root className='ds-canvas'>
      <TopBar>
        <IconButton
          tone='quiet'
          aria-label={isFirst ? 'Close wizard' : 'Back'}
          onClick={isFirst ? onClose : onBack}
        >
          {isFirst ? <CloseIcon /> : <ArrowBackIcon />}
        </IconButton>

        <Box flex={1} minWidth={0}>
          <Typography
            component='h1'
            sx={{
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {heading}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: 'var(--ds-ink-subtle)',
              letterSpacing: '0.01em',
              mt: 0.15,
            }}
          >
            {subtitle || `Step ${stepIndex + 1} of ${steps.length} · ${WIZARD_STEP_LABELS[step]}`}
          </Typography>
        </Box>

        {!isFirst && (
          <IconButton tone='quiet' aria-label='Close wizard' onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </TopBar>

      {/* Compact numbered strip — mobile only; the labeled rail below takes over on desktop */}
      <MobileStepStrip sx={{ display: { xs: 'block', md: 'none' } }}>
        <Stepper
          steps={steps}
          activeId={step}
          reachedIndex={stepIndex}
          onStepClick={handleStepClick}
          orientation='horizontal'
          compact
        />
      </MobileStepStrip>

      <Frame sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 'var(--ds-space-5)', md: 'var(--ds-space-7)' } }}>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Rail>
            <Stepper
              steps={steps}
              activeId={step}
              reachedIndex={stepIndex}
              onStepClick={handleStepClick}
              orientation='vertical'
            />
          </Rail>
        </Box>

        {preview && <Box sx={{ display: { xs: 'block', md: 'none' } }}>{preview}</Box>}

        <Main key={step} role='main' aria-live='polite' sx={{ maxWidth: { xs: '100%', md: 640 } }}>
          {children}
        </Main>

        {preview && (
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <PreviewRail>{preview}</PreviewRail>
          </Box>
        )}
      </Frame>

      <Footer>
        <Button variant='ghost' onClick={isFirst ? onClose : onBack} disabled={saving}>
          {isFirst ? 'Not now' : backLabel}
        </Button>
        {!hideContinue && (
          <Button
            variant='primary'
            onClick={onContinue}
            disabled={!canContinue || saving}
            loading={saving}
          >
            {continueLabel}
          </Button>
        )}
      </Footer>
    </Root>
  )
}
